import cn from "classnames";
import {
    catalogEntityOptionFlags,
    catalogEntityOptionFlagsEnum,
    entityTypesEnum,
    extendedPropertyFlags,
    extendedPropertyFlagsEnum,
} from "helpers/enums";
import { flagVal } from "helpers/system";
import React, { useState } from "react";
import { alert } from "services/alert";
import { count as getLegalPersonCount } from "sources/books/legalPersons";
import { apply } from "sources/catalogs";
import { changeFlags } from "sources/extendedProperties";
import { ItemHiddenSelect, ItemRequiredSelect } from "./itemSelect";

const extendedPropHiddenFlagVal = flagVal(extendedPropertyFlags, extendedPropertyFlagsEnum.Invisible);
const extendedPropRequiredFlagVal = flagVal(extendedPropertyFlags, extendedPropertyFlagsEnum.Required);
const catalogItemHiddenFlagVal = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Invisible);
const catalogItemRequiredFlagVal = flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Required);
const legalPersonPropertyName = "LegalPersonId";
const legalPersonEntityTypes = [entityTypesEnum.TruckingBid, entityTypesEnum.DistributionBid];

const EntityOption = (props) => {
    const { refreshList, kind } = props;
    const { isExtendedProperty, id } = props.data;

    const [intFlags, setFlags] = useState(isExtendedProperty ? props.data.intFlags : props.intFlags);
    const [intRelationFlags, setRelationFlags] = useState(
        isExtendedProperty ? props.data.intRelationFlags : props.intRelationFlags,
    );

    const applyValue = (newValue) => {
        setFlags(newValue);
        return apply({ entityOptionId: props.data.id, flags: newValue });
    };

    const setValue = (newValue) => {
        const isLegalPersonRequiredEnabled =
            legalPersonEntityTypes.includes(kind) &&
            props.data.propertyName === legalPersonPropertyName &&
            (intFlags & catalogItemRequiredFlagVal) !== catalogItemRequiredFlagVal &&
            (newValue & catalogItemRequiredFlagVal) === catalogItemRequiredFlagVal;

        if (!isLegalPersonRequiredEnabled) {
            return applyValue(newValue);
        }

        return getLegalPersonCount().then((res) => {
            if ((res?.count || 0) <= 1) {
                alert({
                    type: "warning",
                    title: "Обязательность не включена",
                    message: `Поле "${props.data.displayName}" можно сделать обязательным, только если вы заполните справочник организаций в разделе "Контргагенты".`,
                });
                return null;
            }

            return applyValue(newValue);
        });
    };

    const commonProps = {
        kind,
        setFlags,
        setRelationFlags,
        intFlags,
        intRelationFlags,
        disabled: !props.roles.isTruckingOrganizationAdmin,
    };

    return (
        <tr>
            <td>{props.data.displayName}</td>
            <td className="w-25">
                {props.data.domainCode ? (
                    <span
                        className={cn("checkbox d-inline-block checkbox--disabled", {
                            "checkbox--checked":
                                (props.data.intFlags & extendedPropHiddenFlagVal) === extendedPropHiddenFlagVal,
                        })}
                    />
                ) : isExtendedProperty ? (
                    <ItemHiddenSelect
                        {...{
                            ...commonProps,
                            name: `${props.data.displayName}_hidden`,
                            flagVal: extendedPropHiddenFlagVal,
                            action: (data) => changeFlags({ id, ...data }).then(refreshList),
                        }}
                    />
                ) : props.data.referencedCatalogId &&
                  [entityTypesEnum.TruckingBid, entityTypesEnum.Cargo].includes(kind) ? (
                    <ItemHiddenSelect
                        {...{
                            ...commonProps,
                            name: `${props.data.displayName}_hidden`,
                            flagVal: catalogItemHiddenFlagVal,
                            action: (data) => apply({ entityOptionId: id, ...data }).then(refreshList),
                        }}
                    />
                ) : (
                    <OptionCheckbox
                        value={flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Invisible)}
                        intFlags={intFlags}
                        onChange={setValue}
                        roles={props.roles}
                        refreshList={props.refreshList}
                        name={`${props.data.displayName}_hidden`}
                    />
                )}
            </td>
            <td className="w-25">
                {props.data.domainCode ? (
                    <span
                        className={cn("checkbox d-inline-block checkbox--disabled", {
                            "checkbox--checked": props.data.isRequired,
                        })}
                    />
                ) : isExtendedProperty ? (
                    <ItemRequiredSelect
                        {...{
                            ...commonProps,
                            flagVal: extendedPropRequiredFlagVal,
                            action: (data) => changeFlags({ id, ...data }).then(refreshList),
                            name: `${props.data.displayName}_required`,
                        }}
                    />
                ) : props.data.referencedCatalogId &&
                  [entityTypesEnum.TruckingBid, entityTypesEnum.Cargo].includes(kind) ? (
                    <ItemRequiredSelect
                        {...{
                            ...commonProps,
                            flagVal: catalogItemRequiredFlagVal,
                            action: (data) => apply({ entityOptionId: id, ...data }).then(refreshList),
                            name: `${props.data.displayName}_required`,
                        }}
                    />
                ) : (
                    <OptionCheckbox
                        value={flagVal(catalogEntityOptionFlags, catalogEntityOptionFlagsEnum.Required)}
                        intFlags={intFlags}
                        onChange={setValue}
                        roles={props.roles}
                        refreshList={props.refreshList}
                        name={`${props.data.displayName}_required`}
                    />
                )}
            </td>
        </tr>
    );
};

const OptionCheckbox = (props) => {
    const { intFlags, value, refreshList } = props;
    const checked = (intFlags & value) === value;
    const { isTruckingOrganizationAdmin } = props.roles;
    const setValue = () => {
        if (!isTruckingOrganizationAdmin) {
            return;
        }
        const newValue = checked ? intFlags - value : intFlags + value;
        return props.onChange(newValue).then(refreshList);
    };
    return (
        <span
            className={cn("checkbox d-inline-block", {
                "checkbox--checked": checked,
                "checkbox--disabled": !isTruckingOrganizationAdmin,
            })}
            name={props.name}
            onClick={setValue}
        />
    );
};

export default EntityOption;
